import React, { useRef, useState, useEffect, useMemo } from 'react';
import { MuiCard } from '@ace/ace-ui-web';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import performancePeerGroupStyles from './PerformancePeerGroup.module.css';
import {
  formatNumberWithCommas,
  roundToOneDecimal,
  isSmallNonZeroValue,
  formatSmallChangeValue,
} from '../../utils/appUtils';
import lang from '../../languages/en.json';
import RankDisplay from '../../components/RankDisplay';
import { useAppSelector } from '../../redux/hooks';
import { selectOverallPerformanceData } from '../../redux/PerformanceReducer/Overall/overallSlice';
import { ChangeYearValue } from '../../models/OverallPerformanceModel';
import { selectOverallLoading } from '../../redux/PerformanceReducer/Overall/overallSlice';
import { selectIsMultiStoreEnabled } from '../../redux/UserReducer/userSlice';
import SkeletonPerformancePGCards from '../../components/skeletons/performance/peergroup/SkeletonPerformancePGCards';

const MAX_BAR_HEIGHT_PX = 90;
const SCROLL_SPEED_MULTIPLIER = 2;

// Helper to create ChangeYearValue from API differencePercent
/* This helper function takes the raw differencePercent from the API and converts it into a 
ChangeYearValue object that includes both the formatted value and the status (positive/negative/neutral) 
for styling purposes. */
export const createChangeValue = (differencePercent: number | undefined): ChangeYearValue => {
  if (differencePercent === undefined || differencePercent === null || isNaN(differencePercent)) {
    return { value: '-', status: 'neutral' };
  }
  return {
    value: `${differencePercent}`,
    status: differencePercent > 0 ? 'positive' : differencePercent < 0 ? 'negative' : 'neutral',
  };
};

// Helper to format currentYtdAmount to string
/* This helper function takes the raw currentYtdAmount data from the API and converts it into a 
string that will be used to be formatted for display purposes. */
export const formatCurrentYear = (amount: number | undefined): string => {
  if (amount === undefined || amount === null || isNaN(amount)) {
    return '-';
  }
  return amount.toString();
};

// Helper to format rank
/* This helper function takes the raw rank data from the API and converts it into a 
string that will be used to be formatted for display purposes. */
export const formatRank = (rank: number | string | null | undefined): string => {
  if (rank === undefined || rank === null || rank === '') {
    return '-';
  }
  return rank.toString();
};

// Helper to format benchmark
/* This helper function takes the raw benchmark data from the API and converts it into a 
string that will be used to be formatted for display purposes. */
export const formatBenchmark = (benchmark: number | null | undefined): string => {
  if (benchmark === undefined || benchmark === null || isNaN(benchmark)) {
    return '-';
  }
  return benchmark.toString();
};

type CardFormat = 'currency' | 'percent' | 'count' | 'avgTransaction';

// Helper function to format value based on card format type
const formatValue = (value: string, format: CardFormat): string => {
  // Handle dash/empty values
  if (value === '-' || value === '' || value === null || value === undefined) {
    return '-';
  }
  switch (format) {
    case 'percent': {
      const num = parseFloat(value);
      if (isNaN(num)) return '-';
      return `${num.toFixed(1)}%`;
    }
    case 'count': {
      const num = parseFloat(value.replace(/,/g, ''));
      if (isNaN(num)) return '-';
      return formatNumberWithCommas(num.toString());
    }
    case 'avgTransaction': {
      const num = parseFloat(value.replace(/,/g, ''));
      if (isNaN(num)) return '-';
      return `$${num.toFixed(2)}`;
    }
    case 'currency':
    default: {
      const num = parseFloat(value.replace(/,/g, ''));
      if (isNaN(num)) return '-';
      return `$${formatNumberWithCommas(Math.round(num).toString())}`;
    }
  }
};

// Helper function to format percentage
const formatPercent = (value: string): string => {
  if (value === '-' || value === '' || value === null || value === undefined) {
    return '-';
  }
  const cleanValue = value.replace('%', '');
  const numericValue = parseFloat(cleanValue);
  if (isNaN(numericValue)) return '-';
  // Check if this is a small non-zero value that would round to 0 (percentages use 1 decimal place)
  if (!isNaN(numericValue) && isSmallNonZeroValue(numericValue, 1)) {
    return formatSmallChangeValue(numericValue, 'PCT', 1);
  }
  return `${formatNumberWithCommas(roundToOneDecimal(cleanValue))}%`;
};

// Helper function to calculate bar heights
const calculateBarHeights = (
  currentYear: string,
  benchmark: string,
  maxBarHeight: number = MAX_BAR_HEIGHT_PX,
) => {
  const currentYearNum = parseFloat(currentYear.replace(/,/g, ''));
  const benchmarkNum = parseFloat(benchmark.replace(/,/g, ''));

  // Handle invalid numbers by defaulting to 0
  if (isNaN(currentYearNum) || isNaN(benchmarkNum)) {
    return {
      currentYearHeight: 0,
      benchmarkHeight: 0,
    };
  }

  const maxValue = Math.max(currentYearNum, benchmarkNum); // Determine the maximum value between the two for scaling

  /* A safety check to determine if either currentYearNum or benchmarkNum <= 0, intentionally have 
  the bars show as 0px height to avoid silent broken state in the browser and implement the correct css code for it */
  if (maxValue <= 0) {
    return { currentYearHeight: 0, benchmarkHeight: 0 };
  }

  const currentYearHeight = (currentYearNum / maxValue) * maxBarHeight;
  const benchmarkHeight = (benchmarkNum / maxValue) * maxBarHeight;

  return {
    currentYearHeight,
    benchmarkHeight,
  };
};

// Card data interface
export interface CardData {
  id: number;
  title: string;
  format: CardFormat;
  rank: string;
  changePercent: ChangeYearValue;
  currentYear: string;
  benchmark: string;
}

// ─── Props interface ─────────────────────────────────────────────────────────

interface PerformancePeerGroupProps {
  /** Pre-built card data for print view — skips Redux read when provided */
  cardData?: CardData[];
  /** When true, hides interactive elements (drag scroll, overflow detection) */
  isPrintView?: boolean;
}

// PerformanceCard - Helper Component (Internal/Co-located)
// This is a sub-component that exists solely to reduce cognitive complexity in the PerformancePeerGroup component.
// It handles rendering of individual performance cards with rank, percentage change, and bar charts.
// Not exported - tightly coupled to parent and uses parent's CSS module and helper functions.
const PRINT_CARD_HEIGHT = 190;
const PRINT_CARD_WIDTH = 232;
const PRINT_MAX_BAR_HEIGHT_PX = 55;
const MULTI_STORE_CARD_HEIGHT = 175;
const PRINT_MULTI_STORE_CARD_HEIGHT = 175;

interface PerformanceCardProps {
  card: CardData;
  isPrintView?: boolean;
  isMultiStore?: boolean;
}

const PerformanceCard: React.FC<PerformanceCardProps> = ({
  card,
  isPrintView = false,
  isMultiStore = false,
}) => {
  let cardHeight: number;
  if (isPrintView) {
    cardHeight = isMultiStore ? PRINT_MULTI_STORE_CARD_HEIGHT : PRINT_CARD_HEIGHT;
  } else {
    cardHeight = isMultiStore ? MULTI_STORE_CARD_HEIGHT : 264;
  }
  const cardWidth = isPrintView ? PRINT_CARD_WIDTH : 239;
  const maxBarHeight = isPrintView ? PRINT_MAX_BAR_HEIGHT_PX : MAX_BAR_HEIGHT_PX;
  return (
    <MuiCard
      key={card.id}
      borderRadius={2}
      elevation={1}
      height={cardHeight}
      padding={2}
      variant="elevation"
      width={cardWidth}>
      <div className={performancePeerGroupStyles.cardContent}>
        {/* Title */}
        <div className={performancePeerGroupStyles.cardTitle}>{card.title.replace(' *', '')}</div>

        {/* Rank and Change Percent Row */}
        <div className={performancePeerGroupStyles.rankPercentRow}>
          {/* Rank */}
          <div className={performancePeerGroupStyles.rankSection}>
            <RankDisplay rank={card.rank} />
            <span className={performancePeerGroupStyles.rankLabel}>
              {lang.performanceOverviewContent.rank}
            </span>
            {isMultiStore && (
              <span
                className={performancePeerGroupStyles.rankSubtextLabel}
                title={lang.performanceOverviewContent.compStoreAvg}>
                {lang.performanceOverviewContent.compStoreAvg}
              </span>
            )}
          </div>

          {/* Change Percent */}
          <div className={performancePeerGroupStyles.changePercentSection}>
            <div className={performancePeerGroupStyles.changePercentWithIcon}>
              {card.changePercent.status !== 'neutral' && (
                <PlayArrowIcon
                  className={`${performancePeerGroupStyles.arrowIcon} ${
                    card.changePercent.status === 'positive'
                      ? performancePeerGroupStyles.arrowUp
                      : performancePeerGroupStyles.arrowDown
                  }`}
                />
              )}
              <div
                className={`${performancePeerGroupStyles.changePercent} ${
                  card.changePercent.status === 'positive'
                    ? performancePeerGroupStyles.positive
                    : card.changePercent.status === 'negative'
                      ? performancePeerGroupStyles.negative
                      : ''
                }`}>
                {formatPercent(card.changePercent.value)}
              </div>
            </div>
            <span className={performancePeerGroupStyles.yoyLabel}>
              {lang.performanceOverviewContent.yoy}
            </span>
            {isMultiStore && (
              <span
                className={performancePeerGroupStyles.yoySubtextLabel}
                title={lang.performanceOverviewContent.chainTotal}>
                {lang.performanceOverviewContent.chainTotal}
              </span>
            )}
          </div>
        </div>

        {/* Bars Container */}
        {isMultiStore ? (
          <div className={performancePeerGroupStyles.multiStoreValueWrapper}>
            <div className={performancePeerGroupStyles.multiStoreValueContainer}>
              <span className={performancePeerGroupStyles.legendDotBlue}></span>
              <span className={performancePeerGroupStyles.barLabel}>
                {formatValue(card.currentYear, card.format)}
              </span>
            </div>
            <span className={performancePeerGroupStyles.multiStoreSubtextLabel}>
              {lang.performanceOverviewContent.chainTotal}
            </span>
          </div>
        ) : (
          <div className={performancePeerGroupStyles.barsContainer}>
            {/* Current Year Bar */}
            <div className={performancePeerGroupStyles.barGroup}>
              <div className={performancePeerGroupStyles.barLabel}>
                {formatValue(card.currentYear, card.format)}
              </div>
              <div
                className={performancePeerGroupStyles.barBlue}
                style={{
                  height: `${calculateBarHeights(card.currentYear, card.benchmark, maxBarHeight).currentYearHeight}px`,
                }}></div>
            </div>

            {/* Benchmark Bar */}
            <div className={performancePeerGroupStyles.barGroup}>
              <div className={performancePeerGroupStyles.barLabel}>
                {formatValue(card.benchmark, card.format)}
              </div>
              <div
                className={performancePeerGroupStyles.barGray}
                style={{
                  height: `${calculateBarHeights(card.currentYear, card.benchmark, maxBarHeight).benchmarkHeight}px`,
                }}></div>
            </div>
          </div>
        )}
      </div>
    </MuiCard>
  );
};

const PerformancePeerGroup: React.FC<PerformancePeerGroupProps> = ({
  cardData: cardDataProp,
  isPrintView = false,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Get real API data from Redux Overall slice (same data as Overall grid)
  const overallPerformanceData = useAppSelector(selectOverallPerformanceData);
  // Peer group uses the same data source as the Overall grid — share its loading state
  const reduxLoading = useAppSelector(selectOverallLoading);
  const isLoading = isPrintView ? false : reduxLoading;
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);

  // Array of card data from API (same source as Overall grid)
  // Uses TSALE, TGP, TGM, TTCNT, TATS from OverallMetrics
  const reduxCardData = useMemo(
    (): CardData[] => [
      {
        id: 1,
        title: lang.pageContent.sales,
        format: 'currency',
        rank: formatRank(overallPerformanceData?.TSALE?.ytdAmountRank),
        changePercent: createChangeValue(overallPerformanceData?.TSALE?.differencePercent),
        currentYear: formatCurrentYear(overallPerformanceData?.TSALE?.currentYtdAmount),
        benchmark: formatBenchmark(overallPerformanceData?.TSALE?.ytdBenchmarkAmount),
      },
      {
        id: 2,
        title: lang.pageContent.grossProfit,
        format: 'currency',
        rank: formatRank(overallPerformanceData?.TGP?.ytdAmountRank),
        changePercent: createChangeValue(overallPerformanceData?.TGP?.differencePercent),
        currentYear: formatCurrentYear(overallPerformanceData?.TGP?.currentYtdAmount),
        benchmark: formatBenchmark(overallPerformanceData?.TGP?.ytdBenchmarkAmount),
      },
      {
        id: 3,
        title: lang.pageContent.grossMargin,
        format: 'percent',
        rank: formatRank(overallPerformanceData?.TGM?.ytdAmountRank),
        changePercent: createChangeValue(overallPerformanceData?.TGM?.differencePercent),
        currentYear: formatCurrentYear(overallPerformanceData?.TGM?.currentYtdAmount),
        benchmark: formatBenchmark(overallPerformanceData?.TGM?.ytdBenchmarkAmount),
      },
      {
        id: 4,
        title: lang.pageContent.transactions,
        format: 'count',
        rank: formatRank(overallPerformanceData?.TTCNT?.ytdAmountRank),
        changePercent: createChangeValue(overallPerformanceData?.TTCNT?.differencePercent),
        currentYear: formatCurrentYear(overallPerformanceData?.TTCNT?.currentYtdAmount),
        benchmark: formatBenchmark(overallPerformanceData?.TTCNT?.ytdBenchmarkAmount),
      },
      {
        id: 5,
        title: lang.pageContent.avgTransaction,
        format: 'avgTransaction',
        rank: formatRank(overallPerformanceData?.TATS?.ytdAmountRank),
        changePercent: createChangeValue(overallPerformanceData?.TATS?.differencePercent),
        currentYear: formatCurrentYear(overallPerformanceData?.TATS?.currentYtdAmount),
        benchmark: formatBenchmark(overallPerformanceData?.TATS?.ytdBenchmarkAmount),
      },
    ],
    [overallPerformanceData],
  );

  // Use prop card data when provided (print view), else use Redux-derived card data
  const cardData = cardDataProp ?? reduxCardData;

  // Check if content is overflowing and if so enable horizontal drag scrolling
  useEffect(() => {
    const checkOverflow = () => {
      if (scrollContainerRef.current) {
        const isContentOverflowing =
          scrollContainerRef.current.scrollWidth > scrollContainerRef.current.clientWidth;
        setIsOverflowing(isContentOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [isLoading]); // Re-check overflow when loading completes. This ensures scrolling is enabled if the loaded content exceeds container width.

  // Calculates how far to scroll as the mouse moves
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current || !isOverflowing) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current || !isOverflowing) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * SCROLL_SPEED_MULTIPLIER; // Multiply by SCROLL_SPEED_MULTIPLIER for faster scrolling
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  // Show skeleton while peer group data is loading
  if (isLoading) {
    return <SkeletonPerformancePGCards />;
  }

  // Print view: render cards in a simple container without scroll/drag/title/legend
  // The parent (PerformancePrintView) wraps these in its own styled grid with print CSS
  if (isPrintView) {
    return (
      <>
        {cardData.map(card => (
          <div key={card.id} className={performancePeerGroupStyles.printCardBorder}>
            <PerformanceCard
              card={card}
              isPrintView
              isMultiStore={isMultiStoreEnabled}
            />
          </div>
        ))}
      </>
    );
  }

  return (
    <div className={performancePeerGroupStyles.performancePeerGroupContainer}>
      <h1 className={performancePeerGroupStyles.title}>{lang.performanceOverviewContent.title}</h1>
      <div
        ref={scrollContainerRef}
        className={`${performancePeerGroupStyles.performancePeerGroupCardWrapper} ${
          isOverflowing ? performancePeerGroupStyles.overflowing : ''
        } ${isDragging ? performancePeerGroupStyles.dragging : ''}`}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}>
        <div
          className={`${performancePeerGroupStyles.performancePeerGroupCardContainer} ${
            isOverflowing ? performancePeerGroupStyles.overflowing : ''
          }`}>
          {cardData.map(card => (
            <PerformanceCard
              key={card.id}
              card={card}
              isMultiStore={isMultiStoreEnabled}
            />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className={performancePeerGroupStyles.legend}>
        <div className={performancePeerGroupStyles.legendItem}>
          <span className={performancePeerGroupStyles.legendDotBlue}></span>
          <span className={performancePeerGroupStyles.legendLabel}>
            {lang.pageContent.currentYear}
          </span>
        </div>
        {!isMultiStoreEnabled && (
          <div className={performancePeerGroupStyles.legendItem}>
            <span className={performancePeerGroupStyles.legendDotGray}></span>
            <span className={performancePeerGroupStyles.legendLabel}>
              {lang.pageContent.percentBenchmark}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformancePeerGroup;
